import { AddAPhotoOutlined, HideImageOutlined, NoPhotographyOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, Grid } from '@mui/material'
import { GreenText } from 'components'
import { NeedsDriversLicensePhotoUploadedOutboundTripDetailDto } from 'dtos/toDoLists/outboundTrips/NeedsDriversLicensePhotoUploadedOutboundTripDetailDto'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { NeedsDriversLicensePhotoUploadedOutboundTripDetailValidator } from 'validators/toDoLists/outboundTrips/NeedsDriversLicensePhotoUploadedOutboundTripDetailValidator'

interface DriversLicensePhotoCaptureDialogProps {
  initialValues: NeedsDriversLicensePhotoUploadedOutboundTripDetailDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: NeedsDriversLicensePhotoUploadedOutboundTripDetailDto) => void
  open: boolean
}

export default function DriversLicensePhotoCaptureDialog({ initialValues, isLoading, onClose, onSave, open }: DriversLicensePhotoCaptureDialogProps) {
  const [objectURL, setObjectURL] = useState<string | null>(null)

  useEffect(() => {
    if (initialValues && initialValues.driversLicensePhotoFile) {
      setObjectURL(URL.createObjectURL(initialValues.driversLicensePhotoFile))
    }

    return () => {
      if (objectURL) {
        URL.revokeObjectURL(objectURL)
        setObjectURL(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={values => onSave(values)}
        validateOnBlur
        validateOnChange
        validationSchema={NeedsDriversLicensePhotoUploadedOutboundTripDetailValidator}
      >
        {({ errors, setFieldValue, values }) => {
          return (
            <Form>
              <DialogContent>
                <Grid container spacing={1} alignItems='center'>
                  <Grid item xs={12}>
                    <DialogTitle>Drivers License Photo Capture</DialogTitle>
                  </Grid>

                  {objectURL ? (
                    <Grid item xs={12}>
                      <img alt="Driver's License" src={objectURL} style={{ width: '100%' }} />
                    </Grid>
                  ) : (
                    <Grid item xs={12} container alignItems='center' justifyContent='center' sx={{ mb: 1 }}>
                      <Grid item sx={{ backgroundColor: '#989898', borderRadius: 4 }}>
                        <NoPhotographyOutlined sx={{ color: '#C4C5C7', fontSize: '256pt', pr: 1 }} />
                      </Grid>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <input
                      accept='image/jpeg,image/jpg,image/png'
                      capture='environment'
                      hidden
                      id='driversLicensePhotoFile'
                      type='file'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          setFieldValue('driversLicensePhotoFile', e.target.files[0])
                          if (objectURL) URL.revokeObjectURL(objectURL)
                          setObjectURL(URL.createObjectURL(e.target.files[0]))
                        }
                      }}
                    />

                    <Button
                      color='primary'
                      endIcon={<AddAPhotoOutlined />}
                      fullWidth
                      onClick={() => document.getElementById('driversLicensePhotoFile')?.click()}
                      variant='outlined'
                    >
                      BROWSE OR TAKE PHOTO
                    </Button>

                    {errors.driversLicensePhotoFile && <FormHelperText error>{errors.driversLicensePhotoFile}</FormHelperText>}
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={!values.driversLicensePhotoFile}
                      endIcon={<HideImageOutlined />}
                      fullWidth
                      onClick={() => {
                        setFieldValue('driversLicensePhotoFile', undefined)
                        if (objectURL) URL.revokeObjectURL(objectURL)
                        setObjectURL(null)
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Photo Previewer:**

Displays a preview of the uploaded photo.

If no photo is uploaded, nothing will be displayed.

**BROWSE OR TAKE PHOTO Button:**

Clicking this button opens the user’s camera app, allowing them to:

   - Take a new photo and upload it.
   - Upload an existing photo from their device.

**DELETE UPLOADED PHOTO Button:**

Clicking this button will:

   - Clear/delete the currently uploaded photo.
   - Remove it from the Photo Previewer.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes.

**SAVE Button:**

Clicking this button will:

   - Save the uploaded photo to the database and close the dialog`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
